import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { UserState } from "store/reducers/user";
import IUser, { MeiStatus, UserStatus, BankStatus } from "Types/User";
import useStyles from "./styles";
import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { Fab } from "@mui/material";
import { MdAdd } from "react-icons/md";
import { AsYouType, parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
import { useNavigate } from "react-router-dom";

// Helper function to normalize and format Brazilian phone numbers
const formatBrazilianPhoneNumber = (phoneNumber: string | null | undefined): string => {
	if (!phoneNumber || phoneNumber.trim() === "") {
		return ls.notDefined;
	}

	try {
		// Remove all non-digit characters except + and spaces
		let cleaned = phoneNumber.replace(/[^\d+\s]/g, "");
		
		// Handle different prefix formats
		if (cleaned.startsWith("+55")) {
			cleaned = cleaned.substring(3); // Remove +55
		} else if (cleaned.startsWith("55")) {
			cleaned = cleaned.substring(2); // Remove 55
		}
		
		// Remove any remaining spaces
		cleaned = cleaned.replace(/\s/g, "");
		
		// Handle Brazilian mobile number formatting
		// Brazilian mobile numbers should be: area code (2) + 9 + phone (8) = 11 digits
		if (cleaned.length === 10) {
			// Missing the "9" after area code, add it
			const areaCode = cleaned.substring(0, 2);
			const phoneNumber = cleaned.substring(2);
			cleaned = `${areaCode}9${phoneNumber}`;
		}
		
		// If we have a valid number of digits (10 or 11 for Brazilian mobile)
		if (cleaned.length >= 10 && cleaned.length <= 11) {
			// Add the country code back for proper parsing
			const fullNumber = `+55${cleaned}`;
			
			// Try to parse and format using libphonenumber-js
			if (isValidPhoneNumber(fullNumber, "BR")) {
				const parsed = parsePhoneNumber(fullNumber, "BR");
				return parsed.formatNational();
			}
			
			// Fallback: use AsYouType for formatting
			return new AsYouType("BR").input(cleaned);
		}
		
		// If the number is too short or too long, return as is
		return phoneNumber;
	} catch (error) {
		console.warn("Error formatting phone number:", phoneNumber, error);
		return phoneNumber || ls.notDefined;
	}
};

const columns = [
	{
		Header: ls.creationDate,
		id: "createdAt",
		accessor: (c: IUser) =>
			dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
		show: true,
		sortMethod: dateSort,
		width: 150
	},
	{
		Header: ls.name,
		id: "fullName",
		show: true,
		accessor: (c: IUser) => c.fullName || ls.notDefined
	},
	{
		Header: ls.phoneNumber,
		id: "phoneNumber",
		show: true,
		accessor: (c: IUser) => formatBrazilianPhoneNumber(c.phoneNumber),
		width: 150
	},
	{
		Header: ls.email,
		id: "email",
		show: false,
		accessor: (c: IUser) => c.email,
		width: 250
	},
	{
		Header: ls.cpf,
		id: "cpf",
		show: true,
		accessor: (c: IUser) => c.cpf,
		width: 140
	},
	{
		Header: ls.company,
		id: "mei",
		show: true,
		accessor: (c: any) => (c.mei ? c.mei.businessName : ls.notDefined)
	},
	{
		Header: ls.status,
		id: "userStatus",
		show: true,
		accessor: (c: IUser) => ls[UserStatus[c.userStatus ?? 1]],
		width: 140,
		Filter: ({ filter, onChange }: any) => {
			return (
				<select
					onChange={event => onChange(event.target.value)}
					style={{ width: "100%" }}
					value={filter?.value ?? "all"}
				>
					<option value="all">Todos</option>
					{Object.keys(UserStatus)
						.filter(c => !isNaN(+c))
						.map((c: any) => (
							<option key={c} value={c}>
								{ls[UserStatus[c]]}
							</option>
						))}
				</select>
			);
		}
	},
	{
		Header: ls.role,
		id: "role",
		show: false,
		accessor: (c: IUser) => ls[c.roles?.find((c: any) => c !== "user")] ?? "",
		width: 140
	}
];

let getUsersDebounded: (() => any) & _.Cancelable;

interface TableState {
	page: number;
	pageSize: number;
	sorted: any[];
	filtered: any[];
	toExport: boolean;
	callback: any;
}

function Users() {
	const navigate = useNavigate();
	const dispatch = useDispatch<any>();
	const classes = useStyles({});

	const state = useSelector<State, UserState>(s => s.user);

	const handleFetchData = (tableState: TableState, instance: any) => {
		const { page, pageSize, toExport, callback } = tableState;

		if (getUsersDebounded) {
			getUsersDebounded.cancel();
		}

		if (!tableState.sorted) {
			tableState.sorted = [];
		}

		const creationDate = tableState.filtered.find(
			(c: any) => c.id === "creationDate"
		);

		if (creationDate?.value) {
			tableState.filtered = tableState.filtered.filter(
				(c: any) => c.id !== "creationDate"
			);

			if (creationDate?.value.startDate) {
				tableState.filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate) {
				tableState.filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
			}
		}

		getUsersDebounded = _.debounce(
			() =>
				dispatch(
					actions.user.getUsers(
						page * pageSize,
						pageSize,
						tableState.filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						tableState.sorted[0]?.id,
						tableState.sorted[0]?.desc,
						toExport,
						callback
					)
				),
			800
		);

		getUsersDebounded();
	};

	return (
		<div>
			<Helmet>
				<title>
					{ls.users} | {ls.appName}
				</title>
			</Helmet>
			<Report
				titleText=""
				manual
				title={ls.clients}
				data={state.items}
				pages={state.pages}
				loading={state.loading}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns.filter(c => c.show).map(c => c.Header)}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
				onRowClicked={data => navigate(`/Dashboard/User/${data._id}`)}
			/>

			<Fab
				color="primary"
				aria-label="Add"
				style={{
					position: "fixed",
					bottom: "16px",
					right: "16px",
					zIndex: 1000
				}}
				onClick={() => navigate("/Dashboard/User/Create")}
			>
				<MdAdd size={24} />
			</Fab>
		</div>
	);
}

export default Users;
